// https://prototype.sqprod.co/#/docs/squareup.franklin.api.FormBlocker.Element.TextElement
import React from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { v4 as uuidv4 } from 'uuid'

import { PaddingInset } from '@cash-design-system/react'
import {
  FormBlockerElementTextElement,
  FormBlockerElementTextElementHorizontalAlignment,
  FormBlockerElementTextElementSize,
  FormBlockerElementTextElementTextColor,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { useTextTokenizer } from '@cash-web/shared-feature-use-text-tokenizer'

export type TextElementProps = { removeTextPadding?: boolean } & FormBlockerElementTextElement

const TextBlock = styled.div<
  TextElementProps & { removeTextPadding: boolean; textColor: FormBlockerElementTextElementTextColor }
>`
  ${props => {
    // handle size
    switch (props.size) {
      case FormBlockerElementTextElementSize.SMALL:
        return css`
          ${props.theme.typography.helpText};
          color: ${props.theme.colors.tertiaryLabel};
        `
      case FormBlockerElementTextElementSize.MEDIUM:
        return css`
          ${props.theme.typography.body};
          color: ${props.theme.colors.secondaryLabel};
        `
      case FormBlockerElementTextElementSize.LARGE:
        return css`
          ${props.theme.typography.numeralSmall};
          color: ${props.theme.colors.label};
        `
      case FormBlockerElementTextElementSize.EXTRA_LARGE:
        return css`
          ${props.theme.typography.hero};
          color: ${props.theme.colors.label};
        `
      default:
        throw new Error(`Failed to render text element, invalid size ${props.size}`)
    }
  }};
  text-align: ${props => {
    // handle alignment
    switch (props.alignment) {
      case FormBlockerElementTextElementHorizontalAlignment.CENTER:
        return 'center'
      case FormBlockerElementTextElementHorizontalAlignment.START:
        return 'left'
      case FormBlockerElementTextElementHorizontalAlignment.END:
        return 'right'
      default:
        throw new Error(`Failed to render text element, invalid alignment ${props.alignment}`)
    }
  }};

  ${props => {
    // handle color
    if (props.textColor === FormBlockerElementTextElementTextColor.WARNING) {
      return css`
        color: ${props.theme.colors.bannerTextError};
      `
    }
    return ''
  }}
`

const ArcadeTextBlock = styled.div<TextElementProps & { textColor: FormBlockerElementTextElementTextColor }>`
  ${props => {
    // handle size
    switch (props.size) {
      case FormBlockerElementTextElementSize.SMALL:
        return css`
          ${props.theme.typography.helpText};
          color: ${props.theme.arcadeColors.semantic.textSubtle};
        `
      case FormBlockerElementTextElementSize.MEDIUM:
        return css`
          ${props.theme.typography.body};
          color: ${props.theme.arcadeColors.semantic.textSubtle};
        `
      case FormBlockerElementTextElementSize.LARGE:
        return css`
          ${props.theme.typography.numeralSmall};
          color: ${props.theme.arcadeColors.semantic.textStandard};
        `
      case FormBlockerElementTextElementSize.EXTRA_LARGE:
        return css`
          ${props.theme.typography.hero};
          color: ${props.theme.arcadeColors.semantic.textStandard};
        `
      default:
        throw new Error(`Failed to render text element, invalid size ${props.size}`)
    }
  }};
  text-align: ${props => {
    // handle alignment
    switch (props.alignment) {
      case FormBlockerElementTextElementHorizontalAlignment.CENTER:
        return 'center'
      case FormBlockerElementTextElementHorizontalAlignment.START:
        return 'left'
      case FormBlockerElementTextElementHorizontalAlignment.END:
        return 'right'
      default:
        throw new Error(`Failed to render text element, invalid alignment ${props.alignment}`)
    }
  }};

  ${props => {
    // handle color
    if (props.textColor === FormBlockerElementTextElementTextColor.WARNING) {
      return css`
        color: ${props.theme.arcadeColors.semantic.textDanger};
      `
    }
    return ''
  }}
`

export function TextElement({
  text,
  size = FormBlockerElementTextElementSize.MEDIUM,
  alignment = FormBlockerElementTextElementHorizontalAlignment.START,
  text_color = FormBlockerElementTextElementTextColor.DEFAULT,
  removeTextPadding = false,
}: TextElementProps): React.ReactElement {
  const textTokenizer = useTextTokenizer()
  const textItems = textTokenizer(text)
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  if (isArcadeMigrationEnabled) {
    return (
      <ArcadeTextBlock size={size} alignment={alignment} textColor={text_color}>
        {textItems}
      </ArcadeTextBlock>
    )
  }

  const textContent = (
    <TextBlock size={size} alignment={alignment} removeTextPadding={removeTextPadding} textColor={text_color}>
      {textItems?.map((item, i) => (
        <span key={uuidv4()}>{item}</span>
      ))}
    </TextBlock>
  )

  return removeTextPadding ? textContent : <PaddingInset>{textContent}</PaddingInset>
}
